import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CategorySelector from "./components/CategorySelector";
import KeywordSelector from "./components/KeywordSelector";
import { businessKeywordsFirst } from "./components/KeywordSelector/keywordValues";
import OfferSaveActions from "../OfferSaveActions";
import addBusinessKeywords from "modules/Business/addBusinessKeywords";
import { getApiErrorMessage } from "utils/apiErrors";
import {
  OFFER_FULFILLMENT_OPTIONS,
  OFFER_FULFILLMENT_VALUES,
} from "modules/Business/modules/Offers/fulfillment";

const schema = yup.object().shape({
  title: yup.string().required("Enter an internal offer title."),
  fulfillment: yup
    .string()
    .required("Select how customers will redeem this offer.")
    .oneOf(OFFER_FULFILLMENT_VALUES, "Select how customers will redeem this offer."),
  category: yup.array().min(1, "Select at least one category."),
  keywords: yup.array().min(1, "Add at least one keyword."),
});

const OfferDescription = ({
  onComplete,
  values,
  onSave,
  onUpdate,
  onValuesChange,
  saveButtonLabel,
}) => {
  const dispatch = useDispatch();
  const business = useSelector((state) => state.business);
  const { enqueueSnackbar } = useSnackbar();

  const {
    register,
    control,
    handleSubmit,
    formState: { dirtyFields, errors, isDirty, touchedFields },
    watch,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {
      ...values.description,
      keywords: businessKeywordsFirst(values.description?.keywords, business.keywords),
    },
    resolver: yupResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const currentValues = watch();
  const keywords = currentValues.keywords;

  useEffect(() => {
    if (touchedFields.title) {
      void trigger("title");
    }
  }, [currentValues.title, touchedFields.title, trigger]);

  useEffect(() => {
    if (dirtyFields.fulfillment) {
      void trigger("fulfillment");
    }
  }, [currentValues.fulfillment, dirtyFields.fulfillment, trigger]);

  useEffect(() => {
    if (dirtyFields.category) {
      void trigger("category");
    }
  }, [currentValues.category, dirtyFields.category, trigger]);

  useEffect(() => {
    onValuesChange?.('description', currentValues, isDirty);
  }, [currentValues, isDirty, onValuesChange]);

  const handleFormSubmit = (data) => {
    onComplete(data);
  };

  const handleSaveKeywords = async () => {
    if (!keywords || keywords.length === 0) {
      enqueueSnackbar("Add at least one offer keyword first.", { variant: "info" });
      return;
    }
    try {
      await addBusinessKeywords(business.businessId, business.keywords || [], keywords, dispatch);
      enqueueSnackbar("Added keywords to your business profile", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, "Couldn’t add keywords to your business profile. Please try again."),
        { variant: "error" }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box mb={4}>
        <Typography variant="h6">Describe your offer</Typography>
        <Typography variant="body2">
          This info will be used to create an offer that reaches the right customers.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.title}>
            <TextField
              id="offer-title"
              label="Title"
              {...register("title")}
              helperText={
                errors.title ? errors.title.message : "This is for your own reference. It won’t be displayed to customers."
              }
              variant="outlined"
              margin="dense"
              error={!!errors.title}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.fulfillment}>
            <Controller
              name="fulfillment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ""}
                  select
                  fullWidth
                  label="Fulfillment"
                  error={!!errors.fulfillment}
                  helperText={
                    errors.fulfillment?.message ||
                    "Select how customers receive this offer."
                  }
                >
                  {OFFER_FULFILLMENT_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.category}>
            <CategorySelector control={control} />
            {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.keywords}>
            <KeywordSelector control={control} />
            <FormHelperText>
              {errors.keywords
                ? errors.keywords.message
                : `Keywords help customers find your offer. Business keywords appear first and are highlighted. Use Add to business keywords to reuse your selected words in future offers.`}
            </FormHelperText>
          </FormControl>
          <Button type="button" color="primary" onClick={handleSaveKeywords}>
            Add to business keywords
          </Button>
        </Grid>
      </Grid>

      <Box mt={2} mb={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <OfferSaveActions
          onUpdate={onUpdate
            ? handleSubmit((data) => onUpdate({ description: data }))
            : undefined}
          onSave={() => onSave({ description: getValues() })}
          saveButtonLabel={saveButtonLabel}
        />
        <Box>
          <Button color="primary" variant="contained" type="submit">
            Next
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default OfferDescription;
